/**
 * Phase 17 — Goods Receipt & Delivery State Machine & Action Transitions
 * Defines legal lifecycle progressions, terminal conditions, and role-based action gating.
 */

import { GrnStatus, GoodsReceiptRecord } from '@/types/domain/grn-management';
import { Permission } from '@/types/domain';

export const VALID_GRN_TRANSITIONS: Record<GrnStatus, GrnStatus[]> = {
  DRAFT: ['RECEIVED', 'CANCELLED'],
  RECEIVED: ['UNDER_INSPECTION', 'ACCEPTED', 'PARTIALLY_ACCEPTED', 'REJECTED', 'QUALITY_HOLD', 'CANCELLED'],
  UNDER_INSPECTION: ['ACCEPTED', 'PARTIALLY_ACCEPTED', 'REJECTED', 'QUALITY_HOLD'],
  QUALITY_HOLD: ['UNDER_INSPECTION', 'ACCEPTED', 'REJECTED'],
  ACCEPTED: [],
  PARTIALLY_ACCEPTED: [],
  REJECTED: [],
  CANCELLED: [],
};

export const TERMINAL_GRN_STATUSES: GrnStatus[] = [
  'ACCEPTED',
  'PARTIALLY_ACCEPTED',
  'REJECTED',
  'CANCELLED',
];

export function canTransitionGrnStatus(from: GrnStatus, to: GrnStatus): boolean {
  if (from === to) return true;
  const allowed = VALID_GRN_TRANSITIONS[from] || [];
  return allowed.includes(to);
}

export function isTerminalGrnStatus(status: GrnStatus): boolean {
  return TERMINAL_GRN_STATUSES.includes(status);
}

export type GrnActionType =
  | 'RECORD_INSPECTION'
  | 'ACCEPT_ALL'
  | 'REJECT_ALL'
  | 'PLACE_ON_HOLD'
  | 'RELEASE_FROM_HOLD'
  | 'SYNC_SAP_MIGO'
  | 'CANCEL';

export interface GrnActionAvailability {
  action: GrnActionType;
  label: string;
  isAvailable: boolean;
  reason?: string;
  isDestructive?: boolean;
}

export function getAvailableGrnActions(
  grn: GoodsReceiptRecord,
  userPermissions: { can?: (perm: Permission) => boolean; role?: string } = {}
): GrnActionAvailability[] {
  const isTerminal = isTerminalGrnStatus(grn.status);
  const canReceive = userPermissions.can ? userPermissions.can('GRN_CREATE') : true;

  return [
    {
      action: 'RECORD_INSPECTION',
      label: 'Record Quality Inspection',
      isAvailable:
        !isTerminal &&
        (grn.status === 'RECEIVED' || grn.status === 'UNDER_INSPECTION' || grn.status === 'QUALITY_HOLD') &&
        canReceive,
      reason: isTerminal ? 'GRN is in terminal state.' : undefined,
    },
    {
      action: 'ACCEPT_ALL',
      label: 'Accept All Goods',
      isAvailable:
        !isTerminal &&
        (grn.status === 'RECEIVED' || grn.status === 'UNDER_INSPECTION') &&
        canReceive,
      reason: isTerminal ? 'GRN already finalized.' : undefined,
    },
    {
      action: 'PLACE_ON_HOLD',
      label: 'Place on Quality Hold',
      isAvailable:
        !isTerminal &&
        (grn.status === 'RECEIVED' || grn.status === 'UNDER_INSPECTION') &&
        canReceive,
    },
    {
      action: 'RELEASE_FROM_HOLD',
      label: 'Release from Quality Hold',
      isAvailable: grn.status === 'QUALITY_HOLD' && canReceive,
    },
    {
      action: 'SYNC_SAP_MIGO',
      label: 'Post SAP MIGO (101)',
      isAvailable: grn.sapSyncStatus !== 'SYNCED' && grn.status !== 'CANCELLED',
      reason: grn.sapSyncStatus === 'SYNCED' ? 'Already posted to SAP MIGO.' : undefined,
    },
    {
      action: 'CANCEL',
      label: 'Cancel GRN',
      isAvailable: !isTerminal && grn.sapSyncStatus !== 'SYNCED' && canReceive,
      isDestructive: true,
      reason: isTerminal ? 'Cannot cancel finalized GRN.' : undefined,
    },
  ];
}
